import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FiAward, FiExternalLink } from 'react-icons/fi';
import DataSection from './Datasection';

const ScholarshipSection = ({ showNotification }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    test_id: '',
    display_order: 0,
    active_status: true,
    image: null,
    start_time: '',
    end_time: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [availableTests, setAvailableTests] = useState([]);
  const [testsLoading, setTestsLoading] = useState(false);

  // Refs for managing focus and preventing unnecessary re-renders
  const titleInputRef = useRef(null);
  const isTypingRef = useRef(false);

  const formatForDateTimeInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const getValidityBadge = (item) => {
    if (!item.start_time && !item.end_time) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
          Unrestricted
        </span>
      );
    }

    const now = new Date();
    const start = item.start_time ? new Date(item.start_time) : null;
    const end = item.end_time ? new Date(item.end_time) : null;

    if (start && now < start) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          Upcoming
        </span>
      );
    } else if (end && now >= end) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
          Ended
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          Active
        </span>
      );
    }
  };

  const columns = [
    { key: 'id', header: 'ID' },
    { key: 'imagePreview', header: 'Image' },
    { key: 'title', header: 'Display Title' },
    { key: 'test_id', header: 'Test ID' },
    { key: 'test_name', header: 'Test Name' },
    { key: 'validityBadge', header: 'Time Window' },
    { key: 'activeStatus', header: 'Status' },
    { key: 'display_order', header: 'Order' }
  ];

  useEffect(() => {
    fetchData();
    fetchScholarshipTests();
  }, []);

  // Use effect to manage focus after rendering
  useEffect(() => {
    if (isModalOpen && titleInputRef.current) {
      // Restore focus if it was lost
      if (document.activeElement !== titleInputRef.current) {
        titleInputRef.current.focus();
      }

      // Prevent losing cursor position
      if (isTypingRef.current) {
        const length = titleInputRef.current.value.length;
        titleInputRef.current.setSelectionRange(length, length);
        isTypingRef.current = false;
      }
    }
  }, [isModalOpen, formData.title]);

  const fetchScholarshipTests = async () => {
    setTestsLoading(true);
    try {
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/test-type-assignments/type/SCHOLARSHIP_TEST');
      const scholarshipTests = response.data;
      
      // Fetch test details for each test_id
      const testDetailsPromises = scholarshipTests.map(test => 
        axios.get(`https://apissc.sainikschoolcadet.com/api/Tests/${test.test_id}`)
      );
      
      const testDetailsResponses = await Promise.all(testDetailsPromises);
      
      const formattedTests = testDetailsResponses.map((res, index) => ({
        test_id: res.data.test_id,
        name: res.data.name,
        assignment_id: scholarshipTests[index].id
      }));
      
      setAvailableTests(formattedTests);
    } catch (error) {
      console.error('Error fetching scholarship tests:', error);
      showNotification('Failed to load available scholarship tests', 'error');
    } finally {
      setTestsLoading(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/scholarship-home');
      
      // Enrich the data with test names and formatted previews
      const enrichedData = await Promise.all(response.data.map(async (item) => {
        let testName = 'Unknown';
        try {
          const testResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/Tests/${item.test_id}`);
          testName = testResponse.data.name || 'Unknown';
        } catch (error) {
          testName = 'Unknown';
        }

        return {
          ...item,
          test_name: testName,
          activeStatus: item.active ? 'Active' : 'Inactive',
          validityBadge: getValidityBadge(item),
          image_path: item.image_path,
          imagePreview: item.image_path ? (
            <img 
              src={item.image_path.startsWith('http') ? item.image_path : `https://apissc.sainikschoolcadet.com/${item.image_path}`} 
              alt={item.title} 
              className="w-12 h-12 object-cover rounded shadow-sm"
            />
          ) : null
        };
      }));
      
      setData(enrichedData);
    } catch (error) {
      console.error('Error fetching scholarships:', error);
      showNotification('Failed to load scholarships', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      test_id: '',
      display_order: 0,
      active_status: true,
      image: null,
      start_time: '',
      end_time: ''
    });
    setImagePreview(null);
    setErrors({});
    setSelectedItem(null);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title || '',
      test_id: item.test_id || '',
      display_order: item.display_order || 0,
      active_status: item.active === true || item.active === 1 || item.activeStatus === 'Active',
      image: null,
      start_time: formatForDateTimeInput(item.start_time),
      end_time: formatForDateTimeInput(item.end_time)
    });

    if (item.image_path) {
      setImagePreview(item.image_path.startsWith('http') ? item.image_path : `https://apissc.sainikschoolcadet.com/${item.image_path}`);
    }
    
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this scholarship?')) {
      try {
        await axios.delete(`https://apissc.sainikschoolcadet.com/api/scholarship-home/${id}`);
        setData(prevData => prevData.filter(item => item.id !== id));
        showNotification('Scholarship deleted successfully');
      } catch (error) {
        console.error('Error deleting scholarship:', error);
        showNotification('Failed to delete scholarship', 'error');
      }
    }
  };

  const handleAdd = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    // Prevent unnecessary state updates during typing
    if (name === 'title') {
      isTypingRef.current = true;
    }

    setFormData(prev => {
      const updatedFormData = { 
        ...prev, 
        [name]: type === 'file' 
          ? files[0] 
          : type === 'checkbox' 
            ? checked 
            : name === 'display_order' 
              ? parseInt(value, 10) || 0 
              : value 
      };
      return updatedFormData;
    });

    // Handle image preview for file inputs
    if (type === 'file') {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.title) {
      newErrors.title = 'Display Title is required';
    }
    
    if (!formData.test_id) {
      newErrors.test_id = 'Test ID is required';
    }
    
    if (formData.display_order < 0) {
      newErrors.display_order = 'Display order must be a positive number';
    }
    
    if (!selectedItem && !formData.image) {
      newErrors.image = 'Please select an image';
    }

    if (formData.start_time && formData.end_time) {
      const start = new Date(formData.start_time);
      const end = new Date(formData.end_time);
      if (end <= start) {
        newErrors.end_time = 'End time must be after start time';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setFormLoading(true);
    
    try {
      let imagePath = selectedItem ? selectedItem.image_path : '';
      
      // If a new image was selected, upload it first
      if (formData.image) {
        const imageFormData = new FormData();
        imageFormData.append('image', formData.image);
        imageFormData.append('title', formData.title || `Scholarship Test ${formData.test_id}`);
        imageFormData.append('tag', 'scholarship-home');
        
        try {
          const imageResponse = await axios.post(
            'https://apissc.sainikschoolcadet.com/api/images',
            imageFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          
          const uploadedPath = imageResponse.data?.path || imageResponse.data?.image?.path || imageResponse.data?.url;
          if (uploadedPath) {
            imagePath = uploadedPath;
          } else {
            throw new Error('Image path not found in response');
          }
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          const errorMsg = imageError.response?.data?.message || imageError.message || 'Failed to upload image. Please try again.';
          setErrors(prev => ({ ...prev, image: errorMsg }));
          showNotification(errorMsg, 'error');
          setFormLoading(false);
          return;
        }
      }
      
      // Now create/update the scholarship with the image path
      const submitData = {
        title: formData.title,
        test_id: parseInt(formData.test_id, 10),
        display_order: formData.display_order,
        active: formData.active_status ? true : false,
        start_time: formData.start_time ? new Date(formData.start_time).toISOString() : null,
        end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null
      };
      
      // Add image path if we have one from upload or keep existing if editing
      if (imagePath) {
        submitData.image_path = imagePath;
      }
      
      let response;
      
      if (selectedItem) {
        // Update existing item
        response = await axios.put(
          `https://apissc.sainikschoolcadet.com/api/scholarship-home/${selectedItem.id}`, 
          submitData
        );
      } else {
        // Create new item
        response = await axios.post(
          'https://apissc.sainikschoolcadet.com/api/scholarship-home',
          submitData
        );
      }
      
      if (response.data) {
        showNotification(selectedItem ? 'Scholarship updated successfully' : 'Scholarship added successfully');
        setIsModalOpen(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error saving scholarship:', error);
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Failed to save. Please try again.' });
      }
      showNotification('Failed to save scholarship', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  // Modal component
  const FormModal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {children}
        </div>
      </div>
    );
  };

  // Get test name from test_id
  const getTestName = (testId) => {
    const test = availableTests.find(test => test.test_id === parseInt(testId, 10));
    return test ? test.name : 'Unknown';
  };

  return (
    <>
      <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600 text-white rounded-lg">
            <FiAward className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Scholarship Tests Workspace is now in CRM</h3>
            <p className="text-xs text-gray-600">
              Manage all scholarship tests, home screen assignments, and view student test results in one place.
            </p>
          </div>
        </div>
        <Link
          to="/scholarship-tests"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition shadow-xs whitespace-nowrap self-start sm:self-auto"
        >
          <span>Open CRM Workspace</span>
          <FiExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>

      <DataSection
        title="Scholarship Home"
        data={data}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        isLoading={isLoading}
        emptyMessage="No scholarships found"
      />
      
      <FormModal isOpen={isModalOpen} onClose={closeModal}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="text-xl font-bold mb-4">
            {selectedItem ? 'Edit Scholarship' : 'Add New Scholarship'}
          </h2>
          
          <div className="form-group">
            <label htmlFor="title" className="block mb-1 font-medium">
              Display Title *
            </label>
            <input
              ref={titleInputRef}
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                }
              }}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title}</p>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="test_id" className="block mb-1 font-medium">
              Scholarship Test *
            </label>
            <select
              id="test_id"
              name="test_id"
              value={formData.test_id}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.test_id ? 'border-red-500' : 'border-gray-300'}`}
              disabled={testsLoading}
            >
              <option value="">Select a scholarship test</option>
              {availableTests.map(test => (
                <option key={test.test_id} value={test.test_id}>
                  {test.test_id} - {test.name}
                </option>
              ))}
            </select>
            {testsLoading && <p className="text-gray-500 text-sm mt-1">Loading tests...</p>}
            {errors.test_id && (
              <p className="text-red-500 text-sm mt-1">{errors.test_id}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label htmlFor="start_time" className="block mb-1 font-medium text-sm">
                Start Time (Optional)
              </label>
              <input
                type="datetime-local"
                id="start_time"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                className="w-full p-2 border rounded border-gray-300 text-sm"
              />
            </div>
            <div className="form-group">
              <label htmlFor="end_time" className="block mb-1 font-medium text-sm">
                End Time (Optional)
              </label>
              <input
                type="datetime-local"
                id="end_time"
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                className={`w-full p-2 border rounded text-sm ${errors.end_time ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.end_time && (
                <p className="text-red-500 text-xs mt-1">{errors.end_time}</p>
              )}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="display_order" className="block mb-1 font-medium">
              Display Order
            </label>
            <input
              type="number"
              id="display_order"
              name="display_order"
              value={formData.display_order}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.display_order ? 'border-red-500' : 'border-gray-300'}`}
              min="0"
            />
            {errors.display_order && (
              <p className="text-red-500 text-sm mt-1">{errors.display_order}</p>
            )}
          </div>
          
          <div className="form-group">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                name="active_status"
                checked={formData.active_status}
                onChange={handleChange}
                className="form-checkbox h-5 w-5"
              />
              <span>Active</span>
            </label>
          </div>
          
          <div className="form-group">
            <label htmlFor="image" className="block mb-1 font-medium">
              Image {!selectedItem && '*'}
            </label>
            <input
              type="file"
              id="image"
              name="image"
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.image ? 'border-red-500' : 'border-gray-300'}`}
              accept="image/*"
            />
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">{errors.image}</p>
            )}
            
            {imagePreview && (
              <div className="mt-2">
                <p className="text-sm font-medium mb-1">Preview:</p>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-40 h-auto object-cover border rounded"
                />
              </div>
            )}
          </div>
          
          {errors.general && (
            <p className="text-red-500 text-sm">{errors.general}</p>
          )}
          
          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              disabled={formLoading}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
              disabled={formLoading}
            >
              {formLoading ? 'Saving...' : selectedItem ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </FormModal>
    </>
  );
};

export default ScholarshipSection;